import { useEffect, useState, forwardRef } from 'react';
import type { RefObject } from 'react';

import {
  logoCatering,
  logoDesktop2025,
  logoNewYear2026,
  logo_dr_21_desktop,
  newYear2026Catering
} from '@images';
import { Picture } from '@ui';
import icons from '@icons';
import { twcls } from '@utils/twcls';
import { Link } from '@compat/router';
import { isBirthday } from '@constants/birthday';

const {
  menu: { black: menuBlackOutlined },
  close: { darkGrey: closeDGOutlined }
} = icons.Outline;

const isNewYear = false;

interface OysterHeaderProps {
  onClickNav: (id: string) => void;
  sliceIndex?: number;
  isCatering?: boolean;
  isHeaderLeft?: boolean;
  phone?: string;
  phoneHref?: string;
  list: Array<{
    id: string;
    title: string;
    ref: RefObject<HTMLDivElement | null>;
    to?: string;
  }>;
  className?: string;
}

const OysterHeader = forwardRef<HTMLDivElement, OysterHeaderProps>(
  (
    {
      list,
      onClickNav,
      isCatering = false,
      isHeaderLeft = false,
      sliceIndex = 4,
      phone,
      phoneHref = 'tel:+74956409640',
      className = ''
    },
    ref
  ) => {
    const [isMobileMenuOpened, setIsMobileMenuOpened] = useState(false);

    useEffect(
      () => () => {
        setIsMobileMenuOpened(false);
      },
      []
    );

    return (
      <div ref={ref} className='sticky top-0 left-0 z-50 sm:static'>
        <div
          className={twcls(
            'z-50 hidden transition-transform sm:flex sm:flex-col -translate-x-full left-0 top-0 fixed w-full h-full p-8 bg-white',
            isMobileMenuOpened && 'translate-x-0'
          )}
        >
          <button
            className='bg-transparent border-none'
            onClick={() => {
              setIsMobileMenuOpened(false);
            }}
          >
            <Picture
              icon={closeDGOutlined}
              pictureClassName='absolute top-5 right-5'
              width={24}
              height={24}
            />
          </button>
          <nav>
            <ul className='flex flex-col gap-3 text-lg'>
              {list
                .filter(item => !!item.title)
                .map(({ id, title, to }) => (
                  <li key={id}>
                    {to ? (
                      <Link
                        to={to}
                        onClick={() => {
                          setIsMobileMenuOpened(false);
                        }}
                      >
                        {title}
                      </Link>
                    ) : (
                      <a
                        href={id}
                        onClick={e => {
                          e.preventDefault();
                          onClickNav(id);
                          setIsMobileMenuOpened(false);
                        }}
                      >
                        {title}
                      </a>
                    )}
                  </li>
                ))}
              {phone && (
                <li>
                  <a href={phoneHref} className='font-medium whitespace-nowrap'>
                    {phone}
                  </a>
                </li>
              )}
            </ul>
          </nav>
        </div>
        {/* newYear2026Catering */}
        <header className='items-center justify-between hidden px-4 py-3 sm:flex'>
          <Link to='/'>
            <Picture
              pictureClassName=''
              icon={
                isBirthday
                  ? logo_dr_21_desktop
                  : isCatering
                  ? isNewYear
                    ? newYear2026Catering
                    : logoCatering
                  : isNewYear
                  ? logoNewYear2026
                  : logoDesktop2025
              }
              width={isCatering ? (isBirthday ? 192 : 175) : 141}
              height={isCatering ? 40 : 32}
            />
          </Link>
          <button
            className='bg-transparent border-none'
            onClick={() => {
              setIsMobileMenuOpened(prev => !prev);
            }}
          >
            <Picture icon={menuBlackOutlined} width={24} height={24} />
          </button>
        </header>
        <header className='sm:hidden  w-full  h-[82px] md:h-[52px] bg-white'>
          <div
            className={twcls(
              'flex items-center justify-between w-full h-full wrapper-container px-15 md:px-6.25 lg:px-10',
              className
            )}
          >
            <Link
              to='/'
              className={twcls(
                'absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2',
                isHeaderLeft && 'static translate-x-0 translate-y-0'
              )}
            >
              <Picture
                pictureClassName={twcls(
                  ' md:[&>img]:w-[141px] md:[&>img]:h-8',
                  isCatering &&
                    (isBirthday
                      ? 'md:[&>img]:w-[192px] md:[&>img]:h-10'
                      : 'md:[&>img]:w-[175px] md:[&>img]:h-10')
                )}
                icon={
                  isBirthday
                    ? logo_dr_21_desktop
                    : isCatering
                    ? isNewYear
                      ? newYear2026Catering
                      : logoCatering
                    : isNewYear
                    ? logoNewYear2026
                    : logoDesktop2025
                }
                width={isCatering ? (isBirthday ? 240 : 220) : 240}
                height={50}
              />
            </Link>
            <nav className={twcls(isHeaderLeft && 'ml-auto mr-5')}>
              <ul
                className={twcls(
                  'flex items-center gap-5 text-lg leading-none md:gap-3 md:text-xs',
                  isCatering && 'text-base gap-4 md:text-sm'
                )}
              >
                {list
                  .slice(0, sliceIndex)
                  .filter(item => !!item.title)
                  .map(({ title, id, to }) => (
                    <li key={id}>
                      {to ? (
                        <Link to={to}>{title}</Link>
                      ) : (
                        <a
                          href={`#${id}`}
                          onClick={e => {
                            e.preventDefault();
                            onClickNav(id);
                          }}
                        >
                          {title}
                        </a>
                      )}
                    </li>
                  ))}
                {phone && (
                  <li>
                    <a
                      href={phoneHref}
                      className='font-medium whitespace-nowrap'
                    >
                      {phone}
                    </a>
                  </li>
                )}
              </ul>
            </nav>
            <nav>
              <ul
                className={twcls(
                  'flex items-center gap-5 text-lg leading-none md:gap-3 md:text-xs',
                  isCatering && 'text-base gap-4 md:text-sm'
                )}
              >
                {list
                  .slice(sliceIndex)
                  .filter(item => !!item.title)
                  .map(({ title, id, to }) => (
                    <li key={id}>
                      {to ? (
                        <Link to={to}>{title}</Link>
                      ) : (
                        <a
                          href={`#${id}`}
                          onClick={e => {
                            e.preventDefault();
                            onClickNav(id);
                          }}
                        >
                          {title}
                        </a>
                      )}
                    </li>
                  ))}
              </ul>
            </nav>
          </div>
        </header>
      </div>
    );
  }
);

export default OysterHeader;
